import React, { useState, useEffect } from 'react';
import { parseEngineOutput } from './lib/parser';
import { ThemeProvider, useTheme, ThemeToggle } from './lib/theme';
import { Badge, Card, SectionTitle, MetricRow, GaugeRing, ConfBar, OutcomeBadge, BAND_COLORS } from './components/ui';
import LeadList from './components/LeadList';
import PipelineAnalytics from './components/PipelineAnalytics';
import { TimingIntelligence, FeedbackOutcomes, ReasoningChains, TierPrediction } from './components/LeadDetail';

var API_URL = "https://supra-intelligence-worker.data-f9f.workers.dev";

// ============================================================
// REP VIEW — What the setter needs to see
// ============================================================
function RepView({ d, lead }) {
  var t = useTheme();
  var showColor = d.scores.showUp >= 0.7 ? '#4ADE80' : d.scores.showUp >= 0.5 ? '#E8A838' : '#EF4444';
  var firstMsg = (d.lead.inbound_messages[0] || {}).text || 'No inbound messages';

  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
    // Header card
    React.createElement(Card, { glow: showColor },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 } },
        React.createElement('div', null,
          React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' } },
            React.createElement('h2', { style: { margin: 0, fontSize: 22, fontWeight: 700, color: t.c.text } }, d.lead.name),
            React.createElement(Badge, { color: d.scores.commitment.color }, (d.scores.commitment.band || '').replace(/_/g, ' ')),
            React.createElement(Badge, { color: '#818CF8' }, d.scores.priority),
            lead && lead.lifecycle_state && React.createElement(Badge, { color: 'rgba(255,255,255,0.3)' }, (lead.lifecycle_state || '').replace(/_/g, ' ')),
            lead && React.createElement(OutcomeBadge, { showed: lead.actual_showed, closed: lead.actual_closed, dealAmount: lead.deal_amount })
          ),
          React.createElement('p', { style: { margin: 0, fontSize: 12, color: t.c.textMuted } },
            d.lead.meeting.date !== 'Not set' ? 'Meeting: ' + d.lead.meeting.date + ' at ' + d.lead.meeting.time : 'No meeting scheduled'
          )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 16 } },
          React.createElement(GaugeRing, { value: d.scores.showUp, max: 1, size: 78, color: showColor, label: 'Show Rate' }),
          React.createElement(GaugeRing, { value: d.scores.ghostRisk.score, max: 1, size: 78, color: '#EF4444', label: 'Ghost Risk', sublabel: d.scores.ghostRisk.cluster })
        )
      )
    ),

    // Reasoning chains
    lead && React.createElement(ReasoningChains, { lead: lead }),

    // Lead's own words
    React.createElement(Card, null,
      React.createElement(SectionTitle, null, "Lead's Own Words"),
      React.createElement('div', { style: { padding: '12px 16px', borderRadius: 6, background: 'rgba(129,140,248,0.06)', borderLeft: '3px solid #818CF8', fontSize: 13, color: t.c.textMuted, lineHeight: 1.6, fontStyle: 'italic' } }, '"' + firstMsg + '"'),
      d.lead.goals.length > 0 && React.createElement('div', { style: { display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' } },
        d.lead.goals.map(function(g, i) { return React.createElement(Badge, { key: i, color: '#4ADE80' }, g); })
      )
    ),

    // Actions
    (d.actions.immediate.length > 0 || d.actions.preCall.length > 0) && React.createElement(Card, { glow: '#EF4444' },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 } },
        React.createElement(SectionTitle, null, 'Action Required'),
        React.createElement(Badge, { color: d.scores.interventionUrgency === 'immediate' ? '#EF4444' : '#E8A838' }, (d.scores.interventionUrgency || '').replace(/_/g, ' '))
      ),
      d.actions.immediate.map(function(a, i) {
        return React.createElement('div', { key: 'i' + i, style: { display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 6, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', marginBottom: 6, alignItems: 'flex-start' } },
          React.createElement('span', { style: { fontSize: 14, flexShrink: 0 } }, '\u26a1'),
          React.createElement('span', { style: { fontSize: 12, color: t.c.textMuted, lineHeight: 1.5 } }, a)
        );
      }),
      d.actions.preCall.map(function(a, i) {
        return React.createElement('div', { key: 'p' + i, style: { display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', marginBottom: 6, alignItems: 'flex-start' } },
          React.createElement('span', { style: { fontSize: 14, flexShrink: 0 } }, '\u2192'),
          React.createElement('span', { style: { fontSize: 12, color: t.c.textMuted, lineHeight: 1.5 } }, a)
        );
      })
    ),

    // Summary
    React.createElement(Card, null,
      React.createElement(SectionTitle, null, 'Situation Summary'),
      React.createElement('p', { style: { fontSize: 13, color: t.c.textMuted, lineHeight: 1.7, margin: 0 } }, d.summary)
    )
  );
}

// ============================================================
// MANAGER VIEW — Scores, flags, rep quality
// ============================================================
function ManagerView({ d, lead }) {
  var t = useTheme();
  var showColor = d.scores.showUp >= 0.7 ? '#4ADE80' : d.scores.showUp >= 0.5 ? '#E8A838' : '#EF4444';
  var fmt = function(s) { return (s || '').replace(/_/g, ' ').toUpperCase(); };

  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
    // Gauges
    React.createElement(Card, null,
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 12 } },
        React.createElement(GaugeRing, { value: d.scores.commitment.score, color: d.scores.commitment.color, label: 'Commitment', sublabel: '/ 100' }),
        React.createElement(GaugeRing, { value: d.scores.showUp, max: 1, color: showColor, label: 'Show Prob' }),
        React.createElement(GaugeRing, { value: d.scores.closeProbability, max: 1, color: '#818CF8', label: 'Close Prob' }),
        React.createElement(GaugeRing, { value: d.scores.readiness, color: '#F472B6', label: 'Readiness', sublabel: '/ 100' }),
        React.createElement(GaugeRing, { value: d.scores.confidence, color: '#38BDF8', label: 'Confidence', sublabel: '/ 100' }),
        React.createElement(GaugeRing, { value: d.scores.ghostRisk.score, max: 1, color: '#EF4444', label: 'Ghost Risk' })
      )
    ),

    // Feedback & Revenue side by side
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 } },
      lead && React.createElement(FeedbackOutcomes, { lead: lead }),
      lead && React.createElement(TierPrediction, { lead: lead })
    ),

    // Domain breakdown + Rep quality
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 } },
      React.createElement(Card, null,
        React.createElement(SectionTitle, null, 'Domain Breakdown'),
        React.createElement(MetricRow, { label: 'Archetype', value: fmt(d.scores.archetype), color: '#818CF8' }),
        React.createElement(MetricRow, { label: 'Decision Stage', value: fmt(d.scores.decisionStage), color: '#E8A838' }),
        React.createElement(MetricRow, { label: 'Quadrant', value: fmt(d.scores.quadrant), color: '#F472B6' }),
        React.createElement(MetricRow, { label: 'Perceived Offer', value: fmt(d.scores.perceivedOffer), color: '#38BDF8' }),
        React.createElement(MetricRow, { label: 'Emotional State', value: fmt(d.scores.emotionalState), color: '#4ADE80' }),
        React.createElement(MetricRow, { label: 'Primary Objection', value: fmt(d.scores.primaryObjection), color: d.scores.primaryObjection === 'none_detected' || d.scores.primaryObjection === 'insufficient_data' ? '#4ADE80' : '#EF4444' }),
        React.createElement(MetricRow, { label: 'Misalignment', value: d.scores.misalignment ? 'TRUE' : 'FALSE', color: d.scores.misalignment ? '#EF4444' : '#4ADE80' })
      ),
      React.createElement(Card, null,
        React.createElement(SectionTitle, null, 'Rep Quality \u2014 ' + d.lead.rep),
        React.createElement(MetricRow, { label: 'Rapport', value: d.scores.rapportStrength + ' / 100', color: d.scores.rapportStrength >= 70 ? '#4ADE80' : '#E8A838' }),
        React.createElement(MetricRow, { label: 'Confirmation', value: fmt(d.scores.confirmationQuality), color: d.scores.confirmationQuality === 'strong' ? '#4ADE80' : '#E8A838' }),
        React.createElement(MetricRow, { label: 'Value Reinforcement', value: d.scores.valueReinforcement + ' / 100', color: d.scores.valueReinforcement >= 70 ? '#4ADE80' : '#E8A838' }),
        React.createElement(MetricRow, { label: 'Friction', value: d.scores.frictionFlag ? 'TRUE' : 'FALSE', color: d.scores.frictionFlag ? '#EF4444' : '#4ADE80' }),
        d.actions.manager.length > 0 && React.createElement('div', { style: { marginTop: 14 } },
          React.createElement(SectionTitle, null, 'Manager Actions'),
          d.actions.manager.map(function(a, i) {
            return React.createElement('div', { key: i, style: { padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 12, color: t.c.textMuted, lineHeight: 1.5 } }, '\u2192 ' + a);
          })
        )
      )
    ),

    // Flags
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 } },
      React.createElement(Card, null,
        React.createElement(SectionTitle, null, 'Green Flags'),
        (d.greenFlags || []).map(function(f, i) {
          return React.createElement('div', { key: i, style: { display: 'flex', gap: 8, padding: '6px 0', alignItems: 'flex-start' } },
            React.createElement('span', { style: { color: '#4ADE80', fontSize: 14, flexShrink: 0 } }, '\u2713'),
            React.createElement('span', { style: { fontSize: 12, color: t.c.textMuted, lineHeight: 1.4 } }, typeof f === 'string' ? f : f.signal || f)
          );
        }),
        (!d.greenFlags || d.greenFlags.length === 0) && React.createElement('span', { style: { fontSize: 12, color: t.c.textFaint } }, 'None detected')
      ),
      React.createElement(Card, null,
        React.createElement(SectionTitle, null, 'Red Flags'),
        (d.redFlags || []).map(function(f, i) {
          return React.createElement('div', { key: i, style: { display: 'flex', gap: 8, padding: '6px 0', alignItems: 'flex-start' } },
            React.createElement('span', { style: { color: '#EF4444', fontSize: 14, flexShrink: 0 } }, '\u2717'),
            React.createElement('span', { style: { fontSize: 12, color: t.c.textMuted, lineHeight: 1.4 } }, typeof f === 'string' ? f : f.signal || f)
          );
        }),
        (!d.redFlags || d.redFlags.length === 0) && React.createElement('span', { style: { fontSize: 12, color: t.c.textFaint } }, 'None detected')
      )
    )
  );
}

// ============================================================
// DEEP DIVE VIEW — Confidence, emotional arc, evidence, conversation
// ============================================================
function DeepDiveView({ d, lead }) {
  var t = useTheme();

  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 16 } },
    // Timing Intelligence
    lead && React.createElement(TimingIntelligence, { lead: lead }),

    // Model Confidence
    React.createElement(Card, null,
      React.createElement(SectionTitle, null, 'Model Confidence by Domain'),
      React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4 } },
        React.createElement(ConfBar, { label: 'Commitment', value: d.confidence.commitment }),
        React.createElement(ConfBar, { label: 'Readiness', value: d.confidence.readiness }),
        React.createElement(ConfBar, { label: 'Marketing', value: d.confidence.marketing }),
        React.createElement(ConfBar, { label: 'Objections', value: d.confidence.objection }),
        React.createElement(ConfBar, { label: 'Emotional', value: d.confidence.emotional }),
        React.createElement(ConfBar, { label: 'Rep Quality', value: d.confidence.repQuality }),
        React.createElement(ConfBar, { label: 'Meta Pred.', value: d.confidence.meta })
      )
    ),

    // No-Show Risk
    React.createElement(Card, { glow: '#EF4444' },
      React.createElement(SectionTitle, null, 'No-Show Risk Analysis'),
      React.createElement(MetricRow, { label: 'Risk Driver', value: (d.noshow.driver || '').replace(/_/g, ' ').toUpperCase(), color: '#EF4444' }),
      React.createElement(MetricRow, { label: 'Critical Point', value: d.noshow.criticalPoint, color: '#E8A838' }),
      React.createElement(MetricRow, { label: 'Prevention', value: d.noshow.prevention, color: '#4ADE80' })
    ),

    // Evidence Summaries
    React.createElement(Card, null,
      React.createElement(SectionTitle, null, 'Domain Evidence'),
      ['commitment', 'readiness', 'marketing', 'objections', 'emotional', 'repQuality'].map(function(key) {
        if (!d.evidenceSummaries[key]) return null;
        return React.createElement('div', { key: key, style: { marginBottom: 10, padding: '10px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', borderLeft: '2px solid rgba(129,140,248,0.3)' } },
          React.createElement('div', { style: { fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 } }, key.replace(/([A-Z])/g, ' $1').trim()),
          React.createElement('div', { style: { fontSize: 11, color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 } }, d.evidenceSummaries[key])
        );
      })
    ),

    // Conversation Replay
    React.createElement(Card, null,
      React.createElement(SectionTitle, null, 'Conversation Replay'),
      d.lead.inbound_messages.map(function(msg, i) {
        return React.createElement('div', { key: 'in' + i, style: { display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 } },
          React.createElement('div', { style: { width: 28, height: 28, borderRadius: '50%', background: 'rgba(74,222,128,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#4ADE80', fontWeight: 700, flexShrink: 0 } }, (d.lead.name || '?')[0]),
          React.createElement('div', { style: { flex: 1 } },
            React.createElement('div', { style: { padding: '10px 14px', borderRadius: '2px 10px 10px 10px', background: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.12)', fontSize: 12, color: t.c.textMuted, lineHeight: 1.6 } }, msg.text),
            msg.timestamp && msg.timestamp !== '1970-01-01T00:00:00.000Z' && React.createElement('div', { style: { fontSize: 9, color: t.c.textGhost, marginTop: 2 } }, new Date(msg.timestamp).toLocaleString())
          )
        );
      }),
      d.lead.outbound_messages.map(function(msg, i) {
        return React.createElement('div', { key: 'out' + i, style: { display: 'flex', gap: 10, alignItems: 'flex-start', justifyContent: 'flex-end', marginBottom: 10 } },
          React.createElement('div', { style: { flex: 1, maxWidth: '85%' } },
            React.createElement('div', { style: { padding: '10px 14px', borderRadius: '10px 2px 10px 10px', background: 'rgba(129,140,248,0.06)', border: '1px solid rgba(129,140,248,0.10)', fontSize: 12, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 } }, msg.text),
            msg.timestamp && React.createElement('div', { style: { fontSize: 9, color: t.c.textGhost, marginTop: 2, textAlign: 'right' } }, new Date(msg.timestamp).toLocaleString())
          ),
          React.createElement('div', { style: { width: 28, height: 28, borderRadius: '50%', background: 'rgba(129,140,248,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#818CF8', fontWeight: 700, flexShrink: 0 } }, d.lead.rep ? d.lead.rep.substring(0, 2) : 'R')
        );
      })
    )
  );
}

// ============================================================
// MAIN APP
// ============================================================
function AppInner() {
  var t = useTheme();
  var leadDataState = useState(null); var leadData = leadDataState[0]; var setLeadData = leadDataState[1];
  var rawLeadState = useState(null); var rawLead = rawLeadState[0]; var setRawLead = rawLeadState[1];
  var viewState = useState('rep'); var view = viewState[0]; var setView = viewState[1];
  var homeTabState = useState('leads'); var homeTab = homeTabState[0]; var setHomeTab = homeTabState[1];

  var handleSelectLead = function(lead) {
    setRawLead(lead);
    var source = lead.analysis_json || lead;
    var parsed = parseEngineOutput(source, lead);
    if (parsed.valid) { setLeadData(parsed); setView('rep'); }
    else { console.error('Parse error:', parsed.error); }
  };

  // HOME SCREEN
  if (!leadData) {
    return React.createElement('div', { style: { minHeight: '100vh', background: t.c.bg, color: t.c.text, fontFamily: "'DM Sans', sans-serif", padding: '24px 20px', transition: 'background 0.3s' } },
      React.createElement('link', { href: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap', rel: 'stylesheet' }),
      React.createElement('div', { style: { position: 'fixed', top: 16, right: 20, zIndex: 50 } }, React.createElement(ThemeToggle)),

      // Logo
      React.createElement('div', { style: { textAlign: 'center', marginBottom: 8 } },
        React.createElement('img', { src: '/supra-human-logo.png', alt: 'Supra Human', style: { width: '75%', maxWidth: 560, objectFit: 'contain', opacity: t.mode === 'dark' ? 1 : 0.85 } })
      ),
      React.createElement('div', { style: { textAlign: 'center', marginBottom: 24 } },
        React.createElement('h1', { style: { margin: 0, fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: t.c.text } },
          React.createElement('span', { style: { color: t.c.textFaint } }, 'SUPRA'), ' INTELLIGENCE'
        ),
        React.createElement('p', { style: { margin: '4px 0 0', fontSize: 11, color: t.c.textGhost, letterSpacing: '0.1em', textTransform: 'uppercase' } }, 'Conversation Analysis Engine v2')
      ),

      // Tabs
      React.createElement('div', { style: { maxWidth: 800, margin: '0 auto' } },
        React.createElement('div', { style: { display: 'flex', gap: 0, marginBottom: 20, background: t.c.bgCard, borderRadius: 8, overflow: 'hidden', border: '1px solid ' + t.c.borderCard, width: 'fit-content' } },
          [{ key: 'leads', label: 'Leads' }, { key: 'analytics', label: 'Pipeline Analytics' }].map(function(tab) {
            return React.createElement('button', { key: tab.key, onClick: function() { setHomeTab(tab.key); }, style: {
              padding: '10px 24px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
              fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.03em', transition: 'all 0.2s',
              background: homeTab === tab.key ? 'rgba(129,140,248,0.15)' : 'transparent',
              color: homeTab === tab.key ? '#818CF8' : t.c.textFaint,
            }}, tab.label);
          })
        )
      ),

      homeTab === 'leads' && React.createElement(LeadList, { onSelect: handleSelectLead }),
      homeTab === 'analytics' && React.createElement(PipelineAnalytics),

      // Footer
      React.createElement('div', { style: { textAlign: 'center', marginTop: 48, paddingBottom: 24 } },
        React.createElement('div', { style: { fontSize: 9, color: t.c.textGhost, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 } }, 'Built by'),
        React.createElement('img', { src: '/ripped-techy-logo.png', alt: 'Ripped Techy', style: { width: '50%', maxWidth: 370, objectFit: 'contain', opacity: t.mode === 'dark' ? 0.7 : 0.6 } })
      )
    );
  }

  // LEAD DETAIL SCREEN
  return React.createElement('div', { style: { minHeight: '100vh', background: t.c.bg, color: t.c.text, fontFamily: "'DM Sans', sans-serif", padding: '24px 20px', transition: 'background 0.3s' } },
    React.createElement('link', { href: 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap', rel: 'stylesheet' }),

    // Top bar
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 } },
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 16 } },
        React.createElement('button', { onClick: function() { setLeadData(null); setRawLead(null); }, style: {
          padding: '6px 12px', borderRadius: 4, border: '1px solid ' + t.c.borderCard,
          background: 'transparent', color: t.c.textFaint, cursor: 'pointer', fontSize: 11, fontFamily: "'DM Sans', sans-serif",
        }}, '\u2190 All Leads'),
        React.createElement('div', null,
          React.createElement('h1', { style: { margin: 0, fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em', color: t.c.text } },
            React.createElement('span', { style: { color: t.c.textFaint } }, 'SUPRA'), ' INTELLIGENCE'
          ),
          React.createElement('p', { style: { margin: '2px 0 0', fontSize: 10, color: t.c.textGhost, letterSpacing: '0.1em', textTransform: 'uppercase' } },
            leadData.lead.name + ' \u2022 ' + (leadData.scores.archetype || '').replace(/_/g, ' ') + ' \u2022 ' + leadData.scores.priority
          )
        )
      ),
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10 } },
        React.createElement('div', { style: { display: 'flex', background: t.c.bgCard, borderRadius: 6, overflow: 'hidden', border: '1px solid ' + t.c.borderCard } },
          [{ key: 'rep', label: 'Rep View' }, { key: 'manager', label: 'Manager View' }, { key: 'deep_dive', label: 'Deep Dive' }].map(function(v) {
            return React.createElement('button', { key: v.key, onClick: function() { setView(v.key); }, style: {
              padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
              fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.03em', transition: 'all 0.2s',
              background: view === v.key ? 'rgba(129,140,248,0.15)' : 'transparent',
              color: view === v.key ? '#818CF8' : t.c.textFaint,
            }}, v.label);
          })
        ),
        React.createElement(ThemeToggle)
      )
    ),

    React.createElement('div', { style: { maxWidth: 800, margin: '0 auto' } },
      view === 'rep' && React.createElement(RepView, { d: leadData, lead: rawLead }),
      view === 'manager' && React.createElement(ManagerView, { d: leadData, lead: rawLead }),
      view === 'deep_dive' && React.createElement(DeepDiveView, { d: leadData, lead: rawLead })
    ),

    React.createElement('div', { style: { textAlign: 'center', marginTop: 48, paddingBottom: 24 } },
      React.createElement('div', { style: { fontSize: 9, color: t.c.textGhost, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 8 } }, 'Built by'),
      React.createElement('img', { src: '/ripped-techy-logo.png', alt: 'Ripped Techy', style: { width: '50%', maxWidth: 370, objectFit: 'contain', opacity: t.mode === 'dark' ? 0.7 : 0.6 } })
    )
  );
}

export default function App() {
  return React.createElement(ThemeProvider, null, React.createElement(AppInner));
}
